import type { AssetSort } from "../../../lib/assetTreeSearch";
import type { AssetId } from "../../../lib/workIds";
import type { AssetTreeNode } from "../../../queries/useAssetTreeQuery";

/**
 * Resolves the branded external Asset identifier for a node identified by
 * its internal numeric `assetTreeId`, searching the raw (unflattened) tree.
 * Used when a feature starts with an internal numeric ID but needs to create
 * a canonical public Asset detail link or send a branded ID over the wire.
 */
export function findAssetIdByTreeId(
  nodes: AssetTreeNode[],
  assetTreeId: number,
): AssetId | null {
  for (const node of nodes) {
    if (node.assetTreeId === assetTreeId) return node.assetId ?? null;
    const found = findAssetIdByTreeId(node.children, assetTreeId);
    if (found) return found;
  }
  return null;
}

/** Shared by the side sheet (IWA-19838) and the full detail page (IWA-19840)
 * so both present the same Kind label for the same value. */
export const ASSET_KIND_LABEL: Record<"ASSET" | "LOCATION", string> = {
  ASSET: "Equipment",
  LOCATION: "Location",
};

export type AssetViewRow = {
  assetId: AssetId | null;
  id: number;
  parentId: number | null;
  depth: number;
  name: string;
  code: string;
  typeId: string | null;
  typeName: string | null;
  sortField: string;
  kind: "ASSET" | "LOCATION" | null;
  sapFunctionalLocationId: string | null;
  manufacturer: string | null;
  model: string | null;
  locationCode: string | null;
  classificationCode: string | null;
  catalogCode: string | null;
};

export function flattenAssetTree(
  nodes: AssetTreeNode[],
  parentId: number | null = null,
  depth = 0,
): AssetViewRow[] {
  return nodes.flatMap((node) => {
    const row: AssetViewRow = {
      assetId: node.assetId ?? null,
      id: node.assetTreeId,
      parentId,
      depth,
      name: node.name,
      code: node.code,
      typeId:
        node.assetTypeId === null || node.assetTypeId === undefined
          ? null
          : String(node.assetTypeId),
      typeName: node.assetTypeName ?? null,
      sortField: node.name,
      kind: node.kind ?? null,
      sapFunctionalLocationId: node.sapFunctionalLocationId ?? null,
      manufacturer: node.manufacturer ?? null,
      model: node.model ?? null,
      locationCode: node.locationName ?? node.locationCode ?? null,
      classificationCode: null,
      catalogCode: null,
    };

    return [
      row,
      ...flattenAssetTree(node.children, node.assetTreeId, depth + 1),
    ];
  });
}

export function directChildCount(rows: AssetViewRow[], parentId: number) {
  return rows.filter((row) => row.parentId === parentId).length;
}

/**
 * Walks the `parentId` chain starting at `row`'s parent (never `row` itself,
 * even when `row.kind === "LOCATION"`) and returns the nearest ancestor whose
 * `kind` is `"LOCATION"`. Returns `null` if the chain reaches the Site root
 * (`parentId === null`) without finding one — Physical Location is then
 * unavailable rather than inferred.
 */
export function findPhysicalLocation(
  rows: AssetViewRow[],
  row: AssetViewRow,
): AssetViewRow | null {
  const rowsById = new Map(rows.map((candidate) => [candidate.id, candidate]));
  let parentId = row.parentId;
  while (parentId !== null) {
    const parent = rowsById.get(parentId);
    if (!parent) return null;
    if (parent.kind === "LOCATION") return parent;
    parentId = parent.parentId;
  }
  return null;
}

/**
 * Resolves the immediate parent row of `row`, or `null` when `row` is a Site
 * root (`parentId === null`) or its parent is not present in `rows`.
 */
export function findParentRow(
  rows: AssetViewRow[],
  row: AssetViewRow,
): AssetViewRow | null {
  if (row.parentId === null) return null;
  return rows.find((candidate) => candidate.id === row.parentId) ?? null;
}

/**
 * Returns the ordered ancestor path from the Site root down to (but
 * excluding) `row` itself — i.e. root-first, parent-last.
 */
export function findAncestorPath(
  rows: AssetViewRow[],
  row: AssetViewRow,
): AssetViewRow[] {
  const rowsById = new Map(rows.map((candidate) => [candidate.id, candidate]));
  const path: AssetViewRow[] = [];
  let parentId = row.parentId;
  while (parentId !== null) {
    const parent = rowsById.get(parentId);
    if (!parent) break;
    path.unshift(parent);
    parentId = parent.parentId;
  }
  return path;
}

export function orderAssetRows(rows: AssetViewRow[]): AssetViewRow[] {
  const byParent = new Map<number | null, AssetViewRow[]>();
  for (const row of rows) {
    const siblings = byParent.get(row.parentId) ?? [];
    siblings.push(row);
    byParent.set(row.parentId, siblings);
  }

  const ordered: AssetViewRow[] = [];
  function visit(parentId: number | null) {
    const children = [...(byParent.get(parentId) ?? [])].sort((left, right) =>
      left.sortField.localeCompare(right.sortField),
    );
    for (const child of children) {
      ordered.push(child);
      visit(child.id);
    }
  }

  visit(null);
  return ordered;
}

/**
 * Applies the user-selected sort to already-filtered rows. Without a sort the
 * incoming order is returned untouched. Rows that compare equal keep their
 * original relative order, and rows with no type name always sort last,
 * whichever direction is active.
 */
export function sortAssetRows(
  rows: AssetViewRow[],
  sort?: AssetSort,
): AssetViewRow[] {
  if (!sort) return rows;
  const direction = sort.dir === "desc" ? -1 : 1;
  return rows
    .map((row, index) => ({ row, index }))
    .sort((left, right) => {
      if (sort.field === "type") {
        const leftType = left.row.typeName ?? "";
        const rightType = right.row.typeName ?? "";
        if (leftType === "" && rightType !== "") return 1;
        if (leftType !== "" && rightType === "") return -1;
        const byType = leftType.localeCompare(rightType, undefined, {
          sensitivity: "base",
        });
        if (byType !== 0) return byType * direction;
      } else {
        const byName = left.row.name.localeCompare(right.row.name, undefined, {
          sensitivity: "base",
        });
        if (byName !== 0) return byName * direction;
      }
      return left.index - right.index;
    })
    .map(({ row }) => row);
}
